import type { SVGProps } from 'react'

type SvgProps = SVGProps<SVGSVGElement> & { size?: number }

const sx = (size: number) => ({ width: size, height: size, display: 'block' as const })

/** Simple geometric icons — original shapes, no third-party artwork. */
export function IconHome({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <path
        d="M4 10.2 12 4l8 6.2V19a1.2 1.2 0 01-1.2 1.2H15v-7.2H9.2V20.2H5.2A1.2 1.2 0 014 19V10.2z"
        stroke={color}
        strokeWidth="1.65"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function IconOrders({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <rect x="5" y="3.5" width="14" height="17" rx="1.5" stroke={color} strokeWidth="1.65" />
      <path d="M8 8h8M8 12h8M8 16h5" stroke={color} strokeWidth="1.65" strokeLinecap="round" />
    </svg>
  )
}

/** Folded map + dashed route + pin (trip / navigation). Used for Trip nav and New Trip. */
export function IconTrip({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      {/* Folded map — 3 panels, perspective */}
      <path
        d="M3.8 16.2V9.8L8.2 8l8-2.2 4.8 2.4v6.5l-4.4 1.6-4-1.4-4.8 1.3z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path
        d="M8.2 8v8.2M16.2 6v8.9"
        stroke={color}
        strokeWidth="1.35"
        strokeLinecap="round"
      />
      {/* Dashed route across folds */}
      <path
        d="M5.2 14.2c2.2-2.2 4-2.5 6.6-1.6 2.4.8 4.2.3 6-1.1 1.5-1.1 2.6-1.4 4-1.2"
        stroke={color}
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="2 1.6"
      />
      {/* Location pin above right fold */}
      <path
        d="M16.9 3.3c-1.15 0-2.1.95-2.1 2.1 0 1.6 2.1 3.95 2.1 3.95s2.1-2.35 2.1-3.95c0-1.15-.95-2.1-2.1-2.1z"
        stroke={color}
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
      <circle cx="16.9" cy="5.35" r="0.85" stroke={color} strokeWidth="1" fill="none" />
    </svg>
  )
}

/** Same artwork as {@link IconTrip} — “New Trip” quick action in the rail. */
export const IconPlusRoute = IconTrip

export function IconInvoice({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <path
        d="M7 3.5h7l4 4.2V20a1.2 1.2 0 01-1.2 1.2H7A1.2 1.2 0 015.8 20V4.7A1.2 1.2 0 017 3.5z"
        stroke={color}
        strokeWidth="1.65"
        strokeLinejoin="round"
      />
      <path d="M14 3.8v4.3h4" stroke={color} strokeWidth="1.65" strokeLinejoin="round" />
      <path d="M8.5 14.5h7M8.5 11h5" stroke={color} strokeWidth="1.65" strokeLinecap="round" />
    </svg>
  )
}

/** Storefront / vendor mark — distinct from document (Invoice) icon. */
export function IconVendor({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <path
        d="M4.5 9.2 6 5.5h12l1.5 3.7v1.3c0 1.1-.9 2-2 2s-2-.9-2-2c0 1.1-.9 2-2 2s-2-.9-2-2c0 1.1-.9 2-2 2s-2-.9-2-2c0 1.1-.9 2-2 2s-2-.9-2-2V9.2z"
        stroke={color}
        strokeWidth="1.65"
        strokeLinejoin="round"
      />
      <path
        d="M6 12.5V19a1 1 0 001 1h10a1 1 0 001-1v-6.5"
        stroke={color}
        strokeWidth="1.65"
        strokeLinejoin="round"
      />
      <path d="M10 20v-4.5h4V20" stroke={color} strokeWidth="1.65" strokeLinejoin="round" />
    </svg>
  )
}

export function IconGridMenu({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <path
        d="M5.5 5.5h4.2v4.2H5.5V5.5zm8.8 0H18v4.2h-4.2V5.5zM5.5 14.3h4.2V18H5.5v-3.8zm8.8 0H18V18h-4.2v-3.8z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function IconChevronDown({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <path d="M6.5 9.2 12 14.5l5.5-5.3" stroke={color} strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function IconChevronUp({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <path d="M6.5 14.7 12 9.4l5.5 5.3" stroke={color} strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function IconAddress({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <path
        d="M12 20s6.5-6.2 6.5-10a6.5 6.5 0 10-13 0C5.5 13.8 12 20 12 20z"
        stroke={color}
        strokeWidth="1.65"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="2" stroke={color} strokeWidth="1.65" />
    </svg>
  )
}

/** Side-view semi — stepped tractor cab + box trailer (reads clearly as truck, not trailer-only). */
export function IconEquipment({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  const sw = 1.65
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      {/* Lower cab block + raised upper cab + trailer — single outline */}
      <path
        d="M3.05 16.5H20.35V9.05H8.05v2.3H7.35V7.05H3.95v4.3H3.05V16.5z"
        stroke={color}
        strokeWidth={sw}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {/* Steer + trailer axle */}
      <circle cx="5.35" cy="18.05" r="1.32" stroke={color} strokeWidth={sw} />
      <circle cx="18.35" cy="18.05" r="1.32" stroke={color} strokeWidth={sw} />
    </svg>
  )
}

export function IconDriver({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <circle cx="12" cy="8" r="3" stroke={color} strokeWidth="1.65" />
      <path
        d="M6.5 19.5c.6-3.6 3.4-5.2 5.5-5.2s4.9 1.6 5.5 5.2"
        stroke={color}
        strokeWidth="1.65"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function IconIntegration({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <circle cx="7" cy="8" r="2.2" stroke={color} strokeWidth="1.65" />
      <circle cx="17" cy="8" r="2.2" stroke={color} strokeWidth="1.65" />
      <circle cx="12" cy="17" r="2.2" stroke={color} strokeWidth="1.65" />
      <path d="M8.7 9.4 10.5 15M15.3 9.4 13.5 15" stroke={color} strokeWidth="1.65" strokeLinecap="round" />
    </svg>
  )
}

export function IconCommission({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <path d="M5.5 18V12M12 18V7M18.5 18v-8" stroke={color} strokeWidth="1.85" strokeLinecap="round" />
    </svg>
  )
}

/** Eight-tooth gear outline — rounded joins, hollow center (matches typical settings icon). */
export function IconSettings({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <path
        d="M12 2.65 14.62 5.67 18.61 5.39 18.33 9.38 21.35 12 18.33 14.62 18.61 18.61 14.62 18.33 12 21.35 9.38 18.33 5.39 18.61 5.67 14.62 2.65 12 5.67 9.38 5.39 5.39 9.38 5.67z"
        stroke={color}
        strokeWidth="1.9"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="3.65" stroke={color} strokeWidth="1.9" />
    </svg>
  )
}

export function IconPlusSheet({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <rect x="5" y="3" width="11" height="15" rx="1.3" stroke={color} strokeWidth="1.65" />
      <path d="M8 10.5h5M10.5 8v5" stroke={color} strokeWidth="1.65" strokeLinecap="round" />
    </svg>
  )
}

export function IconLogout({ size = 22, color = 'currentColor', ...rest }: SvgProps) {
  return (
    <svg {...rest} viewBox="0 0 24 24" style={sx(size)} fill="none" aria-hidden>
      <path
        d="M10 4.5H6.2A1.2 1.2 0 005 5.7v12.6A1.2 1.2 0 006.2 19.5H10"
        stroke={color}
        strokeWidth="1.65"
        strokeLinecap="round"
      />
      <path
        d="M10 12h9M16 8.5 19.5 12 16 15.5"
        stroke={color}
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
